import {
  Combobox as ChakraCombobox,
  createListCollection,
  Portal,
  Spinner,
} from '@chakra-ui/react';
import { forwardRef, useMemo, useRef, useState } from 'react';
import type { StyleProps } from '../../../types';
import { createSyntheticChangeEvent, popFieldWrapperProps } from '../../../utils';
import { FieldWrapper } from '../FieldWrapper';

import { ComboboxDefaultProps, type ComboboxProps } from './Combobox.types';

const Combobox = forwardRef<HTMLDivElement, ComboboxProps>(
  (
    {
      items,
      onChange,
      placeholder,
      onInputChange,
      shouldFilter = true,
      loading,
      renderItem,
      ...props
    },
    ref
  ) => {
    const { poppedProps, otherProps } = popFieldWrapperProps(props);
    const inputRef = useRef<HTMLInputElement>(null);
    const [inputValue, setInputValue] = useState('');

    const filteredItems = useMemo(() => {
      const allItems = items || [];
      if (!shouldFilter || !inputValue) {
        return allItems;
      }
      const lower = inputValue.toLowerCase();
      return allItems.filter((item) => item.label.toLowerCase().includes(lower));
    }, [items, inputValue, shouldFilter]);

    const collection = useMemo(
      () =>
        createListCollection({
          items: filteredItems,
          itemToString: (item) => item.label,
          itemToValue: (item) => item.value,
        }),
      [filteredItems]
    );

    const handleValueChange = (details: { value: Array<string> }) => {
      if (onChange) {
        const syntheticEvent = createSyntheticChangeEvent(
          inputRef.current,
          JSON.stringify(details.value)
        );
        onChange(syntheticEvent);
      }
    };

    const handleInputValueChange = (details: { inputValue: string }) => {
      setInputValue(details.inputValue);
      onInputChange?.(details.inputValue);
    };

    const handleOpenChange = (details: { open: boolean }) => {
      if (details.open) {
        setInputValue('');
      }
    };

    return (
      <FieldWrapper {...poppedProps}>
        <ChakraCombobox.Root
          ref={ref}
          collection={collection}
          onValueChange={handleValueChange}
          onInputValueChange={handleInputValueChange}
          onOpenChange={handleOpenChange}
          openOnClick
          inputBehavior="autocomplete"
          {...ComboboxStyles}
          {...otherProps}
        >
          <ChakraCombobox.Control>
            <ChakraCombobox.Input placeholder={placeholder} {...InputStyles} />
            <ChakraCombobox.IndicatorGroup>
              {loading && <Spinner size="xs" />}
              <ChakraCombobox.ClearTrigger cursor="pointer" />
              <ChakraCombobox.Trigger cursor="pointer" />
            </ChakraCombobox.IndicatorGroup>
          </ChakraCombobox.Control>
          <Portal>
            <ChakraCombobox.Positioner>
              <ChakraCombobox.Content>
                <ChakraCombobox.ItemGroup>
                  {collection.items.map((item) => (
                    <ChakraCombobox.Item key={item.value} item={item} cursor="pointer">
                      {renderItem ? renderItem(item) : item.label}
                      <ChakraCombobox.ItemIndicator />
                    </ChakraCombobox.Item>
                  ))}
                </ChakraCombobox.ItemGroup>
              </ChakraCombobox.Content>
            </ChakraCombobox.Positioner>
          </Portal>
        </ChakraCombobox.Root>
      </FieldWrapper>
    );
  }
);

const ComboboxStyles: StyleProps = {
  width: '100%',
};

const InputStyles: StyleProps = {
  borderColor: { base: 'gray.300', _dark: 'gray.600' },
  bg: { base: 'white', _dark: 'gray.800' },
  cursor: 'pointer',
};

Combobox.displayName = 'Combobox';
Combobox.defaultProps = ComboboxDefaultProps;

export { Combobox };
